import { useContext, useEffect, useState } from "react";
import { t } from "i18next";
import { Button, Card, Tab, Tabs } from "react-bootstrap";
import useDetailsAction from "../actions/details/useDetailsAction";
import CombinationsPage from "@beeldit/ecommerce/combinations/pages/CombinationsPage";
import { ListActionProvider as CombinationsListActionProvider } from "@beeldit/ecommerce/combinations/actions/list/ListActionContext";
import { CreateActionProvider as CombinationsCreateActionProvider } from "@beeldit/ecommerce/combinations/actions/create/CreateActionContext";
import { RemoveActionProvider as CombinationsRemoveActionProvider } from "@beeldit/ecommerce/combinations/actions/remove/RemoveActionContext";
import { EditActionProvider as CombinationsEditActionProvider } from "@beeldit/ecommerce/combinations/actions/edit/EditActionContext";
import { useParams } from "react-router-dom";
import useCreateAction from "@beeldit/ecommerce/products/actions/create/useCreateAction";
import checkRoles from "@beeldit/user-and-access/auth/services/checkRoles";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { DetailsActionProvider as PageDetailsActionProvider } from "@bloonde/cms/cms-pages/actions/details/DetailsActionContext";
import PageEditionPage from "@bloonde/cms/cms-pages/pages/PageEditionPage";
import MainCombinationPage from "@beeldit/ecommerce/combinations/pages/MainCombinationPage";
import GoBack from "@main/panel-components/GoBack";
import { ListActionProvider as OrderListProvider } from "@beeldit/ecommerce/orders/actions/list/ListActionContext";
import { ChangeStatusActionProvider } from "@beeldit/ecommerce/orders/actions/change-status/ChangeStatusActionContext";
import OrdersPage from "@beeldit/ecommerce/orders/pages/OrdersPage";

function ProductDashboardPage(props: {
  producer_type: string;
  roles: string[];
  userProducerIdProp?: any;
  producerSelectorComponent?: any;
}) {
  const {
    producer_type,
    roles,
    userProducerIdProp,
    producerSelectorComponent,
  } = props;

  let { productId } = useParams();

  if (!productId) {
    productId = "0";
  }

  const { user } = useContext(AuthContext);
  let formDefaultValues = {
    producer_id: null,
    producer_type: producer_type,
    product_id: productId,
  };
  if (checkRoles(user, roles)) {
    const producer_id = userProducerIdProp
      .split(".")
      .reduce((acc: any, part: any) => acc && acc[part], user);
    formDefaultValues.producer_id = producer_id;
  }

  const [mainDataTabLoaded, setMainDataTabLoaded] = useState(true);
  const [pageProductTabLoaded, setPageProductTabLoaded] = useState(false);
  const [attributesTabLoaded, setAttributesTabLoaded] = useState(false);
  const [ordersTabLoaded, setOrdersTabLoaded] = useState(false);

  const onSelect = (key: string) => {
    if (key === "main_data") {
      setMainDataTabLoaded(true);
    } else if (key === "page") {
      setPageProductTabLoaded(true);
    } else if (key === "attributes") {
      setAttributesTabLoaded(true);
    } else if (key === "orders") {
      setOrdersTabLoaded(true);
    }
  };

  const {
    initializeFunction,
    actionTemplate: createActionTemplate,
    onSubmitFunction: submitCreateFunction,
  } = useCreateAction(formDefaultValues, producerSelectorComponent);

  const {
    element,
    getFunction,
    onSubmitFunction: submitEditFunction,
    actionTemplate: detailsActionTemplate,
  } = useDetailsAction(productId, producerSelectorComponent);

  useEffect(() => {
    if (productId !== "0") {
      getFunction(productId);
    } else {
      initializeFunction();
    }
  }, [productId]);

  /** Filtro con el que inicializamos las entradas de menú asociadas al header */
  const filters = {
    product_id: productId,
  };
  const formFilterValues = {
    product_id: productId,
  };

  return (
    <Tabs
      defaultActiveKey="main_data"
      id="uncontrolled-tab-example"
      className=""
      onSelect={(key: any) => {
        onSelect(key);
      }}
    >
      <Tab eventKey="main_data" title={t("main_data")}>
        {mainDataTabLoaded && (
          <Card>
            <Card.Header>
              <Card.Title>
                <GoBack /> {t("Product")}
              </Card.Title>
            </Card.Header>

            <Card.Body>
              {productId !== "0" ? detailsActionTemplate : createActionTemplate}
            </Card.Body>

            <Card.Footer>
              <Button
                variant="primary"
                onClick={
                  productId !== "0" ? submitEditFunction : submitCreateFunction
                }
              >
                {t("save_changes")}
              </Button>
            </Card.Footer>
          </Card>
        )}
      </Tab>

      <Tab eventKey="page" title={t("more_info")} disabled={!element?.id}>
        {pageProductTabLoaded && (
          <div className="mb-5">
            <PageDetailsActionProvider>
              <PageEditionPage title={"page"} propPageId={element?.page?.id} />
            </PageDetailsActionProvider>
          </div>
        )}
      </Tab>

      <Tab
        eventKey="attributes"
        title={t("combinations")}
        disabled={!element?.id}
      >
        {attributesTabLoaded && (
          <Card>
            <Card.Body>
              <div className="mb-5">
                <CombinationsListActionProvider key={"main-list-provider"}>
                  <CombinationsEditActionProvider key={"main-edit-provider"}>
                    <MainCombinationPage
                      filters={filters}
                      formDefaultValues={formDefaultValues}
                      formFilterValues={formFilterValues}
                    />
                  </CombinationsEditActionProvider>
                </CombinationsListActionProvider>
              </div>

              <div>
                <CombinationsListActionProvider key={"list-provider"}>
                  <CombinationsRemoveActionProvider key={"remove-provider"}>
                    <CombinationsCreateActionProvider key={"create-provider"}>
                      <CombinationsEditActionProvider key={"edit-provider"}>
                        <CombinationsPage
                          filters={{ ...filters, main: "0" }}
                          formDefaultValues={formDefaultValues}
                          formFilterValues={formFilterValues}
                        />
                      </CombinationsEditActionProvider>
                    </CombinationsCreateActionProvider>
                  </CombinationsRemoveActionProvider>
                </CombinationsListActionProvider>
              </div>
            </Card.Body>
          </Card>
        )}
      </Tab>
      <Tab eventKey="orders" title={t("orders")} disabled={!element?.id}>
        {ordersTabLoaded && (
          <Card>
            <Card.Body>
              <OrderListProvider>
                <ChangeStatusActionProvider>
                  <OrdersPage filters={{product_id: element.id}}></OrdersPage>
                </ChangeStatusActionProvider>
              </OrderListProvider>
            </Card.Body>
          </Card>
        )}
      </Tab>
    </Tabs>
  );
}

export default ProductDashboardPage;
